import { StepDirection } from 'scenes/common/types';
import {
  MotorFlowType,
  MotorFunnelState,
  MotorAllFlowDefinition,
  MotorFlowAction,
  MotorMoveFlows,
  MotorMoveFlowAction,
} from 'scenes/motor/types';

import * as quoteDetailsFlow from './quoteDetails';
import * as addDriverFlow from './addDriver';
import * as coverOptionsFlow from './coverOptions';
import * as policyDetailsFlow from './policyDetails';
import * as addPolicyHolderFlow from './addPolicyHolder';
import * as paymentDetailsFlow from './paymentDetails';
import * as reviewDetailsFlow from './reviewDetails';
import * as reviewDriverFlow from './reviewDriver';
import { initialMotorFunnelState } from './initialMotorFunnelState';
import { MotorFormAnswer } from './types';

const forwardLinks: MotorAllFlowDefinition = {
  [MotorFlowType.QuoteDetails]: quoteDetailsFlow.forwardLinks,
  [MotorFlowType.AddDriver]: addDriverFlow.forwardLinks,
  [MotorFlowType.CoverOptions]: coverOptionsFlow.forwardLinks,
  [MotorFlowType.PolicyDetails]: policyDetailsFlow.forwardLinks,
  [MotorFlowType.AddPolicyHolder]: addPolicyHolderFlow.forwardLinks,
  [MotorFlowType.PaymentDetails]: paymentDetailsFlow.forwardLinks,
  [MotorFlowType.ReviewDetails]: reviewDetailsFlow.forwardLinks,
  [MotorFlowType.ReviewDriver]: reviewDriverFlow.forwardLinks,
};

const backwardLinks: MotorAllFlowDefinition = {
  [MotorFlowType.QuoteDetails]: quoteDetailsFlow.backwardLinks,
  [MotorFlowType.AddDriver]: addDriverFlow.backwardLinks,
  [MotorFlowType.CoverOptions]: coverOptionsFlow.backwardLinks,
  [MotorFlowType.PolicyDetails]: policyDetailsFlow.backwardLinks,
  [MotorFlowType.AddPolicyHolder]: addPolicyHolderFlow.backwardLinks,
  [MotorFlowType.PaymentDetails]: paymentDetailsFlow.backwardLinks,
  [MotorFlowType.ReviewDetails]: reviewDetailsFlow.backwardLinks,
  [MotorFlowType.ReviewDriver]: reviewDriverFlow.backwardLinks,
};

const nextStepType = (
  funnelState: MotorFunnelState,
  action: MotorFlowAction
): Partial<MotorFunnelState> => {
  if (!forwardLinks[funnelState.flow][action.sourceStep]) {
    throw new Error(
      `Missing step for ${action.sourceStep} in ${funnelState.flow} flow`
    );
  }

  const nextLink = forwardLinks[funnelState.flow][action.sourceStep];
  if (typeof nextLink === 'function') {
    return nextLink(funnelState, action);
  }

  return forwardLinks[funnelState.flow][action.sourceStep] as Partial<
    MotorFunnelState
  >;
};

const previousStepType = (
  funnelState: MotorFunnelState,
  action: MotorFlowAction
): Partial<MotorFunnelState> => {
  if (!backwardLinks[funnelState.flow][action.sourceStep]) {
    throw new Error(
      `Missing step for ${action.sourceStep} in ${funnelState.flow} flow`
    );
  }

  const previousLink = backwardLinks[funnelState.flow][action.sourceStep];
  if (typeof previousLink === 'function') {
    return previousLink(funnelState, action);
  }

  return backwardLinks[funnelState.flow][action.sourceStep] as Partial<
    MotorFunnelState
  >;
};

const moveFlows: {
  [s in MotorMoveFlows]: (
    funnelState: MotorFunnelState,
    action: MotorMoveFlowAction
  ) => Partial<MotorFunnelState>;
} = {
  [MotorMoveFlows.EditDriver]: addDriverFlow.editDriver,
  [MotorMoveFlows.RemoveDriver]: addDriverFlow.removeDriver,
  [MotorMoveFlows.EditReviewDriver]: reviewDriverFlow.editDriver,
  [MotorMoveFlows.EditQuoteDetails]: quoteDetailsFlow.editQuoteDetails,
  [MotorMoveFlows.EditPolicyDetails]: policyDetailsFlow.editPolicyDetails,
  [MotorMoveFlows.EditCoverDetails]: coverOptionsFlow.editCoverOptions,
};

const moveToFlow = (
  funnelState: MotorFunnelState,
  action: MotorMoveFlowAction
) =>
  action.targetFlow
    ? moveFlows[action.targetFlow](funnelState, action)
    : funnelState;

const flow = (
  funnelState: MotorFunnelState,
  action: MotorFlowAction
): Partial<MotorFunnelState> => {
  switch (action.direction) {
    case StepDirection.NextIfStepMatches:
      return funnelState.step === action.stepFilter
        ? nextStepType(funnelState, action)
        : funnelState;
    case StepDirection.Next:
      return nextStepType(funnelState, action);
    case StepDirection.Previous:
      return previousStepType(funnelState, action);
    case StepDirection.Beginning:
      return funnelState;
    case StepDirection.MoveFlow:
      return moveToFlow(funnelState, action);
    case StepDirection.StayHere:
      return funnelState;
    case StepDirection.ApiResponse:
      return funnelState;
    default:
      throw new Error(`Unknown action direction '${action.direction}'`);
  }
};

const mergeFormState = (
  state: MotorFunnelState,
  action: MotorFormAnswer
): MotorFunnelState => {
  // Only merge form data via the reducer if option is enabled
  if (!action.autoMergeFormToState) {
    return state;
  }
  if (action.direction === StepDirection.Beginning) {
    return initialMotorFunnelState();
  }
  if (action.direction === StepDirection.ApiResponse) {
    return {
      ...state,
      apiResponses: { ...state.apiResponses, ...action.apiResponses },
    };
  }

  // If a specific slice is nominated, update that
  if (action.sliceToUpdate) {
    return {
      ...state,
      [action.sliceToUpdate]: {
        ...state[action.sliceToUpdate],
        ...action.formValues,
      },
    };
  }
  return {
    ...state,
    [state.flow]: { ...state[state.flow], ...action.formValues },
  };
};

export const targetPage = (
  funnelState: MotorFunnelState,
  action: MotorFlowAction
): MotorFunnelState => ({ ...funnelState, ...flow(funnelState, action) });

export const motorReducer = (
  state: MotorFunnelState,
  action: MotorFlowAction
): MotorFunnelState => targetPage(mergeFormState(state, action), action);
